/**
 * THE WEEK in the little black book (WP-X5, docs/WEEK.md): this week's twist in plain words, your firm's week so
 * far, the honours of the last few weeks in your city, and your firm's own trophy record. Online only; drawn from
 * the server's `week` view (src/online-ui/weekUi.ts). Pure: no DOM, unit-tested.
 */
import { weekStart } from '../../shared/markets';
import type { WeekHonourView, WeekView } from '../../shared/messages/week';
import type { BookEntry, BookPage } from './bookScreen';

const DAY = 86_400_000;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "the week of 5 October" (UTC: the week starts Monday 00:00 UTC). */
export function weekOf(startsAt: number): string {
  const d = new Date(startsAt);
  return `the week of ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "3 days left", "18 hours left", "under an hour left". */
export function timeLeft(endsAt: number, now: number): string {
  const ms = Math.max(0, endsAt - now);
  if (ms >= 2 * DAY) return `${Math.floor(ms / DAY)} days left`;
  const h = Math.floor(ms / 3_600_000);
  return h >= 2 ? `${h} hours left` : h === 1 ? 'an hour left' : 'under an hour left';
}

const n = (v: number): string => Math.round(v).toLocaleString('en-GB');
const plural = (v: number, one: string, many = `${one}s`): string => `${n(v)} ${v === 1 ? one : many}`;

/** The honour's figure in words ("12 crates", "£4,300 of business"); '' for a district lead. */
export function honourFigure(h: Pick<WeekHonourView, 'value' | 'unit'>): string {
  if (!h.unit || h.value <= 0) return '';
  if (h.unit === '£') return `£${n(h.value)} of business`;
  return plural(h.value, h.unit);
}

/** Your firm's week so far, one line each (only what you did). */
export function yourWeekLines(y: WeekView['yours']): string[] {
  const out: string[] = [];
  if (y['value']) out.push(`£${n(y['value'])} of business with the city’s buyers.`);
  const sold = [y['sold-2'] ? `${n(y['sold-2'])} Prime` : '', y['sold-1'] ? `${n(y['sold-1'])} Fine` : '', y['sold-0'] ? `${n(y['sold-0'])} Standard` : ''].filter(Boolean);
  if (sold.length) out.push(`Goods sold: ${sold.join(', ')}.`);
  if (y.crates) out.push(`${plural(y.crates, 'crate')} carried off the quay.`);
  if (y.exports) out.push(`${plural(y.exports, 'rare motor')} handed in at Halloran’s.`);
  if (y.contests) out.push(`${plural(y.contests, 'contest')} for a district won.`);
  if (y.jobs) out.push(`${plural(y.jobs, 'firm’s job')} pulled off.`);
  return out;
}

export function onlineWeekPage(v: WeekView | null, now: number): BookPage {
  const heading = 'The week';
  const lede = 'This week’s twist, and who did best — written down for ever.';
  if (!v) return { heading, lede, entries: [], empty: 'Asking the Evening Post… the week comes up in a moment.' };
  const entries: BookEntry[] = [];
  const THIS = `This week · ${timeLeft(v.endsAt, now)}`;
  if (v.modifier) {
    entries.push({
      id: 'week:modifier',
      section: THIS,
      title: v.modifier.name,
      sub: 'Until Monday',
      mark: 'new',
      kicker: 'The Evening Post',
      heading: v.modifier.name,
      body: [...v.modifier.lines, `It lasts until Monday, when a new week starts with a new twist.`],
    });
  } else {
    entries.push({ id: 'week:modifier', section: THIS, title: 'An ordinary week', sub: 'Nothing out of the way', heading: 'An ordinary week', body: ['Nothing out of the way this week: the usual prices, the usual police.'] });
  }
  const lines = yourWeekLines(v.yours);
  entries.push({
    id: 'week:yours',
    section: THIS,
    title: 'Your firm so far',
    sub: lines.length ? lines[0]!.replace(/\.$/, '') : 'Nothing yet',
    heading: 'Your firm this week',
    body: [
      ...(lines.length ? lines : ['Nothing yet this week.']),
      'At Monday midnight the best firm at each thing — crates off the quay, rare motors, goods sold by grade, contests won, firm’s jobs pulled off, and who runs each district — is written into the book for good. The best independent gets a line too (by name only to themselves).',
    ],
  });
  entries.push({
    id: 'week:trophies',
    section: THIS,
    title: 'Your firm’s honours',
    sub: v.trophies.count ? plural(v.trophies.count, 'honour') : 'none yet',
    heading: 'Your firm’s honours',
    body: v.trophies.count
      ? [`${plural(v.trophies.count, 'honour')} to your firm’s name, all told. They are kept for ever.`, ...v.trophies.recent.map((t) => `· ${t.title} (${weekOf(weekStart(t.week))})`)]
      : ['None yet. Do the most of something in a week and your firm’s name goes in the book for good.'],
  });
  for (const w of v.past) {
    const section = `${w.week === v.week - 1 ? 'Last week' : weekOf(w.startsAt).replace(/^the week/, 'The week')}${w.modifier ? ` · ${w.modifier.name}` : ''}`;
    if (!w.honours.length) {
      entries.push({ id: `week:${w.week}:none`, section, title: 'Nobody made the book', heading: 'A quiet week', body: ['Nobody did enough that week to be written down.'] });
      continue;
    }
    for (const h of w.honours) {
      const fig = honourFigure(h);
      entries.push({
        id: `week:${w.week}:${h.id}`,
        section,
        title: h.title,
        sub: `${h.holder.name}${fig ? ` · ${fig}` : ''}`,
        ...(h.holder.you ? { mark: 'proper' as const } : {}),
        kicker: cap(weekOf(w.startsAt)),
        heading: h.title,
        body: [`${h.holder.you ? (h.holder.tag ? `Your firm, ${h.holder.name}` : 'You') : h.holder.name}${fig ? `, with ${fig}` : ''}.`, ...(h.holder.you ? ['Written down for good: it stays in the book.'] : [])],
      });
    }
  }
  return { heading, lede, entries };
}

function cap(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}
